import type { SxProps, Theme, TypographyVariant } from '@mui/material'
import { Card, CardContent, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BrowserLink } from '@/components/lib/BrowserLink'
import { Cell, CellFrame } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

/**
 * 法的情報へのリンクを表示するセルコンポーネント
 *
 * @description
 * - 特定商取引法に基づく表記へのリンク
 * - 決済方法に関する説明へのリンク
 * - 購入キャンセルに関する注意書き
 * を表示
 *
 * @example
 * ```tsx
 * <LegalInformationLinksCell sx={{ mt: 2 }} />
 * ```
 */
export const LegalInformationLinksCell: Cell<{ sx?: SxProps<Theme> }> = ({
  sx,
}) => {
  return (
    <CellFrame sx={{ pb: 4, ...sx }}>
      <Card elevation={0}>
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          <Stack spacing={2} alignItems="center" justifyContent="center">
            <PurchaseCautionText variant="caption" />
            <Stack direction="row" spacing={5}>
              <WageSettlementMethodLink variant="subtitle1" />
              <SpecificCommercialTransactionLawLink variant="subtitle1" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

/**
 * 購入後のキャンセルに関する注意書きを表示するコンポーネント
 *
 * @description
 * 購入後のキャンセルができない旨をユーザーに通知
 *
 * @param props.variant - テキストのタイポグラフィバリアント（デフォルト: 'caption'）
 * @param props.sx - カスタムスタイル
 *
 * @example
 * ```tsx
 * <PurchaseCautionText variant="body2" sx={{ mt: 2 }} />
 * ```
 */
export const PurchaseCautionText: Cell<{
  sx?: SxProps<Theme>
  variant?: TypographyVariant
}> = ({ sx, variant = 'caption' }) => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <Typography variant={variant} color="text.secondary" sx={sx}>
      {t('cancelPurchaseNote')}
    </Typography>
  )
}

/**
 * 決済方法に関する説明へのリンクを表示するコンポーネント
 *
 * @description
 * 決済方法の詳細な説明が記載されているヘルプページへのリンクを提供
 *
 * @param props.variant - リンクのタイポグラフィバリアント（デフォルト: 'subtitle1'）
 * @param props.sx - カスタムスタイル
 *
 * @example
 * ```tsx
 * <WageSettlementMethodLink variant="body1" sx={{ textDecoration: 'underline' }} />
 * ```
 */
export const WageSettlementMethodLink: Cell<{
  sx?: SxProps<Theme>
  variant?: TypographyVariant
}> = ({ sx, variant = 'subtitle1' }) => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <BrowserLink
      variant={variant}
      href={getHelpUrl('wageSettlementMethod')}
      sx={sx}
    >
      {t('wageSettlementMethod')}
    </BrowserLink>
  )
}

/**
 * 特定商取引法に基づく表記へのリンクを表示するコンポーネント
 *
 * @description
 * 特定商取引法に基づく表記が記載されているページへのリンクを提供
 *
 * @param props.variant - リンクのタイポグラフィバリアント（デフォルト: 'subtitle1'）
 * @param props.sx - カスタムスタイル
 *
 * @example
 * ```tsx
 * <SpecificCommercialTransactionLawLink variant="body1" sx={{ color: 'primary.main' }} />
 * ```
 */
export const SpecificCommercialTransactionLawLink: Cell<{
  sx?: SxProps<Theme>
  variant?: TypographyVariant
}> = ({ sx, variant = 'subtitle1' }) => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <BrowserLink
      variant={variant}
      href={getHelpUrl('specifiedCommercialTransactionLaw')}
      sx={sx}
    >
      {t('specifiedCommercialTransactionLaw')}
    </BrowserLink>
  )
}
